/**
 * Safe lead-source attribution (separate from heard_about / referral_source).
 * Optional, length-limited, no fingerprinting.
 */

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const STORAGE_KEY = "sokno_attribution_v1";
const MAX_LEN = 120;
const MAX_PATH = 200;

function clamp(value, max = MAX_LEN) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  return s.slice(0, max);
}

function safePath(value) {
  const s = clamp(value, MAX_PATH);
  if (!s) return null;
  // Paths only — reject schemes / protocol-relative URLs.
  if (/^[a-z][a-z0-9+.-]*:/i.test(s) || s.startsWith("//")) return null;
  if (!s.startsWith("/")) return `/${s}`.slice(0, MAX_PATH);
  return s;
}

export function readAttributionFromSearch(search = "") {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const out = {};
  for (const key of UTM_KEYS) {
    const v = clamp(params.get(key));
    if (v) out[key] = v;
  }
  return out;
}

export function captureAttributionFromLocation(location = window.location) {
  try {
    const fromQuery = readAttributionFromSearch(location.search || "");
    const existing = loadStoredAttribution() || {};
    const next = {
      ...existing,
      ...fromQuery,
    };
    if (!next.landing_route) {
      next.landing_route = safePath(`${location.pathname || "/"}${location.search || ""}`.split("?")[0]);
    }
    if (typeof document !== "undefined" && document.referrer) {
      try {
        const refUrl = new URL(document.referrer);
        // Store only same-site path or external host label — not full arbitrary URLs.
        if (refUrl.origin === location.origin) {
          next.referring_route = safePath(refUrl.pathname);
        } else {
          next.referring_route = clamp(refUrl.hostname, 80);
        }
      } catch {
        /* ignore bad referrer */
      }
    }
    persistAttribution(next);
    return next;
  } catch {
    return {};
  }
}

export function persistAttribution(data) {
  try {
    const cleaned = sanitizeAttribution(data);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
  } catch {
    /* private mode / quota — fail open */
  }
}

export function loadStoredAttribution() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return sanitizeAttribution(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function sanitizeAttribution(input = {}) {
  const out = {};
  for (const key of UTM_KEYS) {
    const v = clamp(input[key]);
    if (v) out[key] = v;
  }
  const landing = safePath(input.landing_route);
  const referring = clamp(input.referring_route, MAX_PATH);
  if (landing) out.landing_route = landing;
  if (referring) out.referring_route = referring;
  return out;
}

/** Merge attribution into API payload without overwriting heard_about. */
export function withAttribution(payload, attribution) {
  const clean = sanitizeAttribution(attribution || loadStoredAttribution() || {});
  if (!Object.keys(clean).length) return payload;
  return { ...payload, ...clean };
}
